// r24 Product Catalog — full A5 product page renderer.
// Composes hero + trust strip + long description + pricing tiers + process +
// FAQ + related products. Consumed by /naati/products/$sku and /en mirror.

import ProductDepthBlock from "@/components/ProductDepthBlock";
import { Link } from "@tanstack/react-router";
import { Check, Clock, ShieldCheck, Stamp, Globe2, ArrowRight } from "lucide-react";
import type { NaatiProduct } from "@/data/naati-products";
import {
  NAATI_PRICE_MIN_THB, NAATI_PRICE_MAX_THB,
  NAATI_TURNAROUND_MIN, NAATI_TURNAROUND_MAX,
  NAATI_LEAD_TRANSLATOR_CPN, relatedProducts,
} from "@/data/naati-products";
import NaatiProductCTA from "./NaatiProductCTA";
import NaatiAuthorByline from "./NaatiAuthorByline";
import { CrossSellStrip } from "@/components/CrossSellStrip";
import ConsultantAdvisoryBlock from "@/components/ConsultantAdvisoryBlock";

interface Props {
  product: NaatiProduct;
  locale: "th" | "en";
  faqs: { q: string; a: string }[];
}

export default function NaatiProductPage({ product, locale, faqs }: Props) {
  const isTh = locale === "th";
  const name = isTh ? product.nameTh : product.nameEn;
  const catLabel = isTh ? product.categoryLabelTh : product.categoryLabelEn;
  const longDesc = isTh ? product.longDescTh : product.longDescEn;
  const bullets = isTh ? product.bulletTh : product.bulletEn;
  const steps = isTh ? product.processStepsTh : product.processStepsEn;
  const related = relatedProducts(product.slug, 6);

  const L = isTh ? {
    trust: "รับรองโดยนักแปล NAATI CPN + ทนายไทย",
    priceLabel: "ราคาต่อชุดมาตรฐาน",
    turnLabel: "ระยะเวลาส่ง",
    days: "วันทำการ",
    acceptedBy: "ยอมรับโดย",
    usedFor: "ใช้ยื่นได้กับ",
    whatIncluded: "รวมในราคานี้",
    tiers: "ทางเลือกความเร็ว",
    tier5: "5 วัน — Priority", tier6: "6 วัน — Standard", tier7: "7 วัน — Economy",
    tier5Desc: "เร่งด่วน — ประทับตราภายใน 5 วัน", tier6Desc: "ยอดนิยม — สมดุลระหว่างราคา/ความเร็ว", tier7Desc: "งบประหยัดสุด — เหมาะกับงานล่วงหน้า",
    howTitle: "ขั้นตอนการสั่งซื้อ 4 ขั้น",
    faqTitle: "คำถามที่พบบ่อย",
    relatedTitle: "สินค้าที่เกี่ยวข้อง",
    breadcrumb: ["หน้าหลัก", "NAATI", "สินค้าและบริการ"],
    directAnswer: `ราคา ${NAATI_PRICE_MIN_THB.toLocaleString()}–${NAATI_PRICE_MAX_THB.toLocaleString()} บาท · ${NAATI_TURNAROUND_MIN}–${NAATI_TURNAROUND_MAX} วันทำการ · รวมค่าตราประทับ NAATI CPN + ตรวจโดยทนาย + ส่งไฟล์ทั่วโลก`,
    whyBlock: "ทำไมต้องเลือก NYC",
  } : {
    trust: "Certified by NAATI CPN translator + Thai attorney",
    priceLabel: "Price per standard set",
    turnLabel: "Turnaround",
    days: "business days",
    acceptedBy: "Accepted by",
    usedFor: "Used for",
    whatIncluded: "Included in this price",
    tiers: "Speed options",
    tier5: "5 days — Priority", tier6: "6 days — Standard", tier7: "7 days — Economy",
    tier5Desc: "Fastest — stamp within 5 business days", tier6Desc: "Most popular — balanced speed/price", tier7Desc: "Budget-friendly for advance planners",
    howTitle: "How to order — 4 steps",
    faqTitle: "Frequently asked questions",
    relatedTitle: "Related products",
    breadcrumb: ["Home", "NAATI", "Products"],
    directAnswer: `THB ${NAATI_PRICE_MIN_THB.toLocaleString()}–${NAATI_PRICE_MAX_THB.toLocaleString()} · ${NAATI_TURNAROUND_MIN}–${NAATI_TURNAROUND_MAX} business days · Includes NAATI CPN stamp, attorney review, worldwide delivery.`,
    whyBlock: "Why choose NYC",
  };

  const catalogPath = isTh ? "/naati/products" : "/en/naati/products";
  const naatiHubPath = isTh ? "/translate/naati" : "/en/translate/naati";

  return (
    <main className="min-h-screen bg-background pb-28 md:pb-16">
      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="mx-auto max-w-6xl px-4 pt-6 text-xs text-slate-500">
        <ol className="flex flex-wrap gap-1">
          <li><Link to={isTh ? "/" : "/en"} className="hover:text-amber-600">{L.breadcrumb[0]}</Link></li>
          <li>›</li>
          <li><Link to={naatiHubPath} className="hover:text-amber-600">{L.breadcrumb[1]}</Link></li>
          <li>›</li>
          <li><Link to={catalogPath} className="hover:text-amber-600">{L.breadcrumb[2]}</Link></li>
          <li>›</li>
          <li aria-current="page" className="text-slate-700">{name}</li>
        </ol>
      </nav>

      {/* Hero */}
      <section className="mx-auto max-w-6xl px-4 pt-6">
        <div className="grid gap-8 lg:grid-cols-[1.15fr_1fr]">
          <div>
            <div className="mb-3 flex flex-wrap gap-2 text-[11px] font-bold uppercase tracking-widest">
              <span className="rounded-full bg-amber-100 px-3 py-1 text-amber-800">{catLabel}</span>
              <span className="rounded-full bg-emerald-100 px-3 py-1 text-emerald-800">
                <ShieldCheck className="mr-1 inline h-3 w-3" /> NAATI {NAATI_LEAD_TRANSLATOR_CPN}
              </span>
              <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-700">SKU {product.sku}</span>
            </div>
            <h1 className="font-display text-3xl font-extrabold leading-tight text-navy-deep sm:text-4xl lg:text-5xl">
              {isTh ? `แปล ${name} รับรอง NAATI` : `NAATI-Certified ${name} Translation`}
            </h1>
            <p data-speakable className="direct-answer mt-4 rounded-xl border-l-4 border-amber-400 bg-amber-50/50 p-4 text-[15px] leading-relaxed text-slate-700">
              {L.directAnswer}
            </p>

            {/* Price + turnaround band */}
            <div className="price-band mt-5 grid gap-3 sm:grid-cols-2">
              <div className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-4">
                <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-700">{L.priceLabel}</div>
                <div className="mt-1 font-display text-2xl font-extrabold text-emerald-800">
                  THB {NAATI_PRICE_MIN_THB.toLocaleString()}–{NAATI_PRICE_MAX_THB.toLocaleString()}
                </div>
                <div className="mt-0.5 text-xs text-slate-600">{isTh ? "รวมค่าตราประทับ + ทนายตรวจ" : "Stamp + attorney review included"}</div>
              </div>
              <div className="rounded-2xl border border-sky-200 bg-gradient-to-br from-sky-50 to-white p-4">
                <div className="text-[11px] font-bold uppercase tracking-wider text-sky-700">{L.turnLabel}</div>
                <div className="mt-1 flex items-center gap-2 font-display text-2xl font-extrabold text-sky-800">
                  <Clock className="h-5 w-5" /> {NAATI_TURNAROUND_MIN}–{NAATI_TURNAROUND_MAX} {L.days}
                </div>
                <div className="mt-0.5 text-xs text-slate-600">{isTh ? "เร่งด่วน 2–3 วันได้ (มีค่าธรรมเนียมเพิ่ม)" : "2–3 day express available (surcharge)"}</div>
              </div>
            </div>

            <div className="mt-5">
              <NaatiProductCTA product={product} locale={locale} variant="inline" />
            </div>
            <div className="mt-3 text-xs text-slate-500">
              <ShieldCheck className="mr-1 inline h-3 w-3 text-emerald-600" />
              {L.trust}
            </div>
          </div>

          {/* Hero visual */}
          <aside className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-amber-50/40 p-6">
            <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-amber-700">
              <Stamp className="h-4 w-4" /> {isTh ? "ตัวอย่างตราประทับ" : "Stamp preview"}
            </div>
            <div className="mt-4 flex aspect-square items-center justify-center rounded-xl border-2 border-dashed border-amber-300 bg-white p-8">
              <div className="text-center">
                <div className="mx-auto flex h-32 w-32 items-center justify-center rounded-full border-4 border-blue-800 bg-white text-blue-900">
                  <div className="text-center">
                    <div className="text-[9px] font-bold uppercase tracking-widest">NAATI</div>
                    <div className="mt-1 text-[7px] leading-tight">Certified<br/>Practitioner</div>
                    <div className="mt-1 border-t border-blue-800 pt-1 text-[8px] font-bold">{NAATI_LEAD_TRANSLATOR_CPN}</div>
                  </div>
                </div>
                <div className="mt-3 text-[10px] uppercase tracking-widest text-slate-500">
                  {isTh ? "ประทับสีน้ำเงินสด + ลายเซ็น" : "Wet blue seal + signature"}
                </div>
              </div>
            </div>
            <ul className="mt-5 space-y-1.5 text-[12px] text-slate-700">
              <li className="flex gap-2"><Globe2 className="h-3.5 w-3.5 text-sky-600 mt-0.5" /> {isTh ? "ส่ง PDF + กระดาษทั่วโลก DHL/EMS" : "PDF + hard copy worldwide via DHL/EMS"}</li>
              <li className="flex gap-2"><ShieldCheck className="h-3.5 w-3.5 text-emerald-600 mt-0.5" /> {isTh ? "ตรวจสอบได้บน naati.com.au directory" : "Verifiable at naati.com.au directory"}</li>
              <li className="flex gap-2"><Check className="h-3.5 w-3.5 text-amber-600 mt-0.5" /> {isTh ? "ทนายไทยลงนามตรวจก่อนประทับตรา" : "Thai attorney signs off before stamping"}</li>
            </ul>
          </aside>
        </div>
      </section>

      {/* Bullets / What's included */}
      <section className="mx-auto mt-12 max-w-6xl px-4">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">{L.whatIncluded}</h2>
        <ul className="mt-4 grid gap-3 sm:grid-cols-2">
          {bullets.map((b) => (
            <li key={b} className="flex gap-3 rounded-xl border border-slate-200 bg-white p-4">
              <Check className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
              <span className="text-sm text-slate-700">{b}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Long description */}
      <section className="mx-auto mt-12 max-w-6xl px-4">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">
          {isTh ? `เกี่ยวกับ ${name}` : `About ${name}`}
        </h2>
        <p className="mt-4 text-[15px] leading-relaxed text-slate-700">{longDesc}</p>
        <p className="mt-3 text-[15px] leading-relaxed text-slate-700">
          {isTh ? product.doctype.notesTh : product.doctype.notesEn}
        </p>
      </section>

      {/* Speed tiers */}
      <section className="mx-auto mt-12 max-w-6xl px-4">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">{L.tiers}</h2>
        <div className="mt-4 grid gap-3 md:grid-cols-3">
          {[
            { label: L.tier5, desc: L.tier5Desc, price: NAATI_PRICE_MAX_THB, tone: "amber" },
            { label: L.tier6, desc: L.tier6Desc, price: 1900, tone: "emerald", badge: isTh ? "แนะนำ" : "Popular" },
            { label: L.tier7, desc: L.tier7Desc, price: NAATI_PRICE_MIN_THB, tone: "sky" },
          ].map((t) => (
            <div key={t.label} className={`relative rounded-2xl border p-5 ${
              t.tone === "emerald" ? "border-emerald-300 bg-emerald-50/40 ring-2 ring-emerald-200"
              : t.tone === "amber" ? "border-amber-300 bg-amber-50/40"
              : "border-sky-300 bg-sky-50/40"
            }`}>
              {t.badge && (
                <span className="absolute -top-2 right-4 rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white">
                  {t.badge}
                </span>
              )}
              <div className="font-display text-lg font-extrabold text-navy-deep">{t.label}</div>
              <div className="mt-1 text-xs text-slate-600">{t.desc}</div>
              <div className="mt-3 font-display text-2xl font-extrabold text-navy-deep">
                THB {t.price.toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Accepted-by strip */}
      <section className="mx-auto mt-12 max-w-6xl px-4">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">{L.acceptedBy}</h2>
        <div className="mt-4 flex flex-wrap gap-2">
          {product.acceptedBy.map((a) => (
            <span key={a} className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm">
              ✓ {a}
            </span>
          ))}
        </div>
        {product.usedFor.length > 0 && (
          <>
            <h3 className="mt-6 font-display text-lg font-bold text-navy-deep">{L.usedFor}</h3>
            <div className="mt-2 flex flex-wrap gap-2">
              {product.usedFor.map((u) => (
                <span key={u} className="rounded-md bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-800 ring-1 ring-amber-200">
                  {u}
                </span>
              ))}
            </div>
          </>
        )}
      </section>

      {/* How to order */}
      <section className="mx-auto mt-12 max-w-6xl px-4">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">{L.howTitle}</h2>
        <ol className="mt-4 grid gap-3 md:grid-cols-2">
          {steps.map((s, i) => (
            <li key={s} className="flex gap-3 rounded-xl border border-slate-200 bg-white p-4">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-navy-deep font-display font-bold text-white">
                {i + 1}
              </span>
              <span className="text-sm text-slate-700">{s}</span>
            </li>
          ))}
        </ol>
      </section>

      {/* Author byline (E-E-A-T) */}
      <div className="mx-auto max-w-6xl px-4">
        <NaatiAuthorByline lang={locale} />
      </div>

      {/* FAQ */}
      <section className="mx-auto mt-6 max-w-6xl px-4">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">{L.faqTitle}</h2>
        <div className="mt-4 space-y-3">
          {faqs.map((f) => (
            <details key={f.q} className="speakable-faq group rounded-xl border border-slate-200 bg-white p-4 open:bg-amber-50/30">
              <summary className="cursor-pointer list-none font-display font-bold text-navy-deep">
                <span className="mr-2 text-amber-600 group-open:hidden">+</span>
                <span className="mr-2 hidden text-amber-600 group-open:inline">−</span>
                {f.q}
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-slate-700">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Related */}
      <CrossSellStrip source="naati" locale={locale} />

      <section className="mx-auto mt-12 max-w-6xl px-4">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">{L.relatedTitle}</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {related.map((r) => (
            <Link
              key={r.sku}
              to={isTh ? "/naati/products/$sku" : "/en/naati/products/$sku"}
              params={{ sku: r.slug }}
              className="group rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-amber-400 hover:shadow-md"
            >
              <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700">
                {isTh ? r.categoryLabelTh : r.categoryLabelEn}
              </div>
              <div className="mt-1 font-display font-extrabold text-navy-deep group-hover:text-amber-600">
                {isTh ? r.nameTh : r.nameEn}
              </div>
              <div className="mt-2 text-xs text-slate-500">
                THB {NAATI_PRICE_MIN_THB.toLocaleString()}–{NAATI_PRICE_MAX_THB.toLocaleString()} · {NAATI_TURNAROUND_MIN}–{NAATI_TURNAROUND_MAX} {L.days}
              </div>
              <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-amber-700">
                {isTh ? "ดูรายละเอียด" : "View details"} <ArrowRight className="h-3 w-3" />
              </div>
            </Link>
          ))}
        </div>
      </section>


      {/* Sticky mobile CTA */}
      <NaatiProductCTA product={product} locale={locale} variant="sticky-mobile" />
      <ProductDepthBlock cluster="naati" locale={locale === "th" ? "th" : "en"} />
      <ConsultantAdvisoryBlock locale={locale === "th" ? "th" : "en"} topic="NAATI" />
    </main>
  );
}
